import * as React from "react";
import { FileText, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

export type PreviewDocumentLink = {
	key: string;
	label: string;
	url: string;
	sourceLabel?: string;
};

type ResolvedDocumentUrl = {
	previewUrl: string | null;
	openInNewTabUrl: string | null;
	printUrl: string | null;
};

const DOCUMENT_PREVIEW_PROXY_PATH = "/api/document-preview-proxy";

function isLocalDevelopmentHost(hostname: string) {
	return (
		hostname === "localhost" ||
		hostname === "127.0.0.1" ||
		hostname === "::1" ||
		hostname.endsWith(".localhost")
	);
}

function buildDocumentPreviewProxyUrl(url: string) {
	const trimmedUrl = String(url ?? "").trim();
	if (!trimmedUrl) return null;
	return `${DOCUMENT_PREVIEW_PROXY_PATH}?${new URLSearchParams({ url: trimmedUrl }).toString()}`;
}

function printPdfByUrl(url: string) {
	const iframe = document.createElement("iframe");
	let cleanedUp = false;

	const cleanup = () => {
		if (cleanedUp) return;
		cleanedUp = true;
		iframe.remove();
	};

	iframe.setAttribute("aria-hidden", "true");
	iframe.style.position = "fixed";
	iframe.style.right = "0";
	iframe.style.bottom = "0";
	iframe.style.width = "0";
	iframe.style.height = "0";
	iframe.style.border = "0";
	iframe.style.opacity = "0";
	iframe.style.pointerEvents = "none";
	iframe.onload = () => {
		const frameWindow = iframe.contentWindow;
		if (!frameWindow) {
			cleanup();
			return;
		}

		const finishPrint = () => {
			window.setTimeout(cleanup, 0);
		};

		window.addEventListener("focus", finishPrint, { once: true });
		try {
			frameWindow.addEventListener("afterprint", finishPrint, { once: true });
		} catch {
			// Safari does not always expose afterprint on embedded PDF viewers.
		}

		try {
			frameWindow.focus();
			frameWindow.print();
		} catch {
			cleanup();
			return;
		}

		window.setTimeout(cleanup, 10_000);
	};
	iframe.src = url;
	document.body.appendChild(iframe);
}

function resolveDocumentUrl(url: string): ResolvedDocumentUrl {
	const trimmedUrl = String(url ?? "").trim();
	if (!trimmedUrl) {
		return { previewUrl: null, openInNewTabUrl: null, printUrl: null };
	}

	try {
		const parsedUrl = new URL(trimmedUrl, window.location.origin);
		const isSameOrigin = parsedUrl.origin === window.location.origin;
		const isLocalDevelopmentUrl = isLocalDevelopmentHost(parsedUrl.hostname);
		const isHttpUrl = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
		if (!isHttpUrl) {
			return { previewUrl: null, openInNewTabUrl: null, printUrl: null };
		}

		if (!isSameOrigin && !isLocalDevelopmentUrl && parsedUrl.protocol !== "https:") {
			return { previewUrl: null, openInNewTabUrl: null, printUrl: null };
		}

		const safeHref = parsedUrl.href;
		const previewUrl =
			isSameOrigin || isLocalDevelopmentUrl
				? safeHref
				: buildDocumentPreviewProxyUrl(safeHref);

		return {
			previewUrl,
			openInNewTabUrl: safeHref,
			printUrl: previewUrl,
		};
	} catch {
		return { previewUrl: null, openInNewTabUrl: null, printUrl: null };
	}
}

export function DocumentsPreviewButton({
	documents,
	title,
	description,
	triggerLabel,
	emptyFallback,
}: {
	documents: PreviewDocumentLink[];
	title: string;
	description?: string;
	triggerLabel?: string;
	emptyFallback?: React.ReactNode;
}) {
	const [isDialogOpen, setIsDialogOpen] = React.useState(false);
	const [activeDocKey, setActiveDocKey] = React.useState<string | null>(null);

	const documentUrls = React.useMemo(
		() => new Map(documents.map((doc) => [doc.key, resolveDocumentUrl(doc.url)])),
		[documents],
	);

	const firstDoc = documents[0] ?? null;
	const activeDoc =
		documents.find((document) => document.key === activeDocKey) ?? firstDoc ?? null;
	const activeDocUrls = activeDoc
		? documentUrls.get(activeDoc.key) ?? resolveDocumentUrl(activeDoc.url)
		: { previewUrl: null, openInNewTabUrl: null, printUrl: null };
	const activeDocPreviewUrl = activeDocUrls.previewUrl;

	React.useEffect(() => {
		if (!activeDocKey && firstDoc) {
			setActiveDocKey(firstDoc.key);
		}
	}, [activeDocKey, firstDoc]);

	const handlePrintActiveDocument = React.useCallback(() => {
		if (!activeDocUrls.printUrl) return;
		printPdfByUrl(activeDocUrls.printUrl);
	}, [activeDocUrls.printUrl]);

	if (documents.length === 0) {
		return <>{emptyFallback ?? <span className="text-xs text-muted-foreground">—</span>}</>;
	}

	return (
		<div className="flex items-center gap-1.5">
			<Button
				size="xs"
				variant="outline"
				className="h-7 gap-1.5"
				onClick={() => setIsDialogOpen(true)}
			>
				<FileText className="size-3.5" />
				{triggerLabel ?? `Docs (${documents.length})`}
			</Button>
			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="h-[86vh] max-w-[96vw] overflow-hidden p-0 sm:max-w-[96vw]">
					<div className="flex h-full min-h-0 flex-col">
						<DialogHeader className="border-b px-5 py-4 text-left">
							<DialogTitle>{title}</DialogTitle>
							{description ? <DialogDescription>{description}</DialogDescription> : null}
						</DialogHeader>
						<div className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:flex-row lg:items-start">
							<div className="flex w-full shrink-0 flex-col gap-3 lg:w-72">
								<div className="flex flex-col gap-2">
									{documents.map((document) => {
										const isActive = activeDoc?.key === document.key;
										return (
											<Button
												key={document.key}
												variant={isActive ? "default" : "outline"}
												size="sm"
												className="h-10 w-full justify-between rounded-lg px-3"
												onClick={() => setActiveDocKey(document.key)}
											>
												<span className="truncate">{document.label}</span>
												<span className="text-[10px] uppercase opacity-80">
													{document.sourceLabel ?? "PDF"}
												</span>
											</Button>
										);
									})}
								</div>

								{activeDoc ? (
									<div className="flex flex-col gap-2 rounded-xl border bg-muted/20 p-2">
										<Button
											size="sm"
											className="h-10 w-full gap-1.5 rounded-lg"
											disabled={!activeDocUrls.printUrl}
											onClick={handlePrintActiveDocument}
										>
											<Printer className="size-4" />
											Print PDF
										</Button>
										<Button
											size="sm"
											variant="secondary"
											className="h-10 w-full rounded-lg"
											disabled={!activeDocUrls.openInNewTabUrl}
											onClick={() => {
												if (!activeDocUrls.openInNewTabUrl) return;
												window.open(activeDocUrls.openInNewTabUrl, "_blank", "noopener,noreferrer");
											}}
										>
											Open in New Tab
										</Button>
									</div>
								) : null}
							</div>

							<div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-muted/20 p-3 lg:p-4">
								{activeDoc && activeDocPreviewUrl ? (
									<div className="mx-auto h-full min-h-full w-full max-w-[1200px] overflow-hidden rounded-lg border bg-white shadow-sm">
										<object
											key={activeDoc.key}
											data={activeDocPreviewUrl}
											title={activeDoc.label}
											aria-label={activeDoc.label}
											type="application/pdf"
											className="h-full min-h-[68vh] w-full"
										>
											<div className="flex h-full min-h-[68vh] items-center justify-center p-6 text-sm text-muted-foreground">
												Preview unavailable for this PDF.
											</div>
										</object>
									</div>
								) : activeDoc ? (
									<div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
										Preview unavailable for this PDF.
									</div>
								) : (
									<div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
										No documents available.
									</div>
								)}
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
